import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getDeviationTypes, createDeviationType, updateDeviationType, deleteDeviationType } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, AlertCircle } from 'lucide-react';

export default function DeviationsPage() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [deviations, setDeviations] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingDeviation, setEditingDeviation] = useState(null);
    const [formData, setFormData] = useState({ name: '', type: 'negative', active: true });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchDeviations();
    }, []);

    const fetchDeviations = async () => {
        try {
            const res = await getDeviationTypes();
            setDeviations(res.data);
        } catch (error) {
            toast.error(t('deviations.failed_load'));
        } finally {
            setLoading(false);
        }
    };

    const openCreateDialog = () => {
        setEditingDeviation(null);
        setFormData({ name: '', type: 'negative', active: true });
        setDialogOpen(true);
    };

    const openEditDialog = (deviation) => {
        setEditingDeviation(deviation);
        setFormData({ name: deviation.name, type: deviation.type, active: deviation.active });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            toast.error(t('deviations.name_required'));
            return;
        }

        setSaving(true);
        try {
            if (editingDeviation) {
                await updateDeviationType(editingDeviation.id, formData);
                toast.success(t('deviations.type_updated'));
            } else {
                await createDeviationType(formData);
                toast.success(t('deviations.type_created'));
            }
            setDialogOpen(false);
            fetchDeviations();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('deviations.failed_save'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (deviation) => {
        if (!window.confirm(t('deviations.delete_confirm', { name: deviation.name }))) return;

        try {
            await deleteDeviationType(deviation.id);
            toast.success(t('deviations.type_deleted'));
            fetchDeviations();
        } catch (error) {
            toast.error(t('deviations.failed_delete'));
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-foreground border-t-transparent animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="deviations-page">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                <div>
                    <h1 className="font-heading text-4xl font-bold tracking-tight">{t('deviations.title')}</h1>
                    <p className="text-muted-foreground mt-1">{t('deviations.subtitle')}</p>
                </div>
                <Button
                    className="uppercase tracking-wider"
                    onClick={openCreateDialog}
                    data-testid="add-deviation-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('deviations.add_type')}
                </Button>
            </div>

            {/* Info */}
            <div className="flex items-start gap-2 p-4 bg-muted border border-border">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">
                    <strong>{t('deviations.negative_prefix')}</strong> {t('deviations.info_text')}
                </p>
            </div>

            {/* Deviations List */}
            <Card className="border border-border shadow-none">
                <CardContent className="p-0">
                    {deviations.length === 0 ? (
                        <div className="p-8 text-center text-muted-foreground">
                            <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-30" />
                            <p>{t('deviations.no_deviations')}</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>{t('deviations.col_name')}</th>
                                    <th>{t('deviations.col_type')}</th>
                                    <th>{t('deviations.col_status')}</th>
                                    <th className="text-right">{t('deviations.col_actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deviations.map((deviation) => (
                                    <tr key={deviation.id}>
                                        <td className="font-medium">{deviation.name}</td>
                                        <td>
                                            <span className={`inline-block px-2 py-1 text-xs uppercase tracking-wider ${
                                                deviation.type === 'negative'
                                                    ? 'bg-destructive text-white'
                                                    : 'bg-green-600 text-white'
                                            }`}>
                                                {deviation.type}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`inline-block px-2 py-1 text-xs uppercase tracking-wider ${
                                                deviation.active
                                                    ? 'bg-foreground text-background'
                                                    : 'bg-muted text-muted-foreground'
                                            }`}>
                                                {deviation.active ? t('common.active') : t('common.inactive')}
                                            </span>
                                        </td>
                                        <td className="text-right">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => openEditDialog(deviation)}
                                                data-testid={`edit-deviation-${deviation.id}`}
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDelete(deviation)}
                                                className="text-destructive hover:text-destructive"
                                                data-testid={`delete-deviation-${deviation.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            {editingDeviation ? t('deviations.edit_title') : t('deviations.new_title')}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="label-uppercase mb-2 block">{t('deviations.label_name')}</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder={t('deviations.placeholder_name')}
                                data-testid="deviation-name-input"
                            />
                        </div>

                        <div>
                            <Label className="label-uppercase mb-2 block">{t('deviations.label_type')}</Label>
                            <Select
                                value={formData.type}
                                onValueChange={(v) => setFormData({ ...formData, type: v })}
                            >
                                <SelectTrigger data-testid="deviation-type-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="negative">{t('deviations.negative_absence')}</SelectItem>
                                    <SelectItem value="positive">{t('deviations.positive_extra')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="flex items-center justify-between">
                            <Label className="label-uppercase">{t('deviations.label_active')}</Label>
                            <Switch
                                checked={formData.active}
                                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                                data-testid="deviation-active-switch"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button onClick={handleSave} disabled={saving} data-testid="save-deviation-btn">
                            {saving ? t('common.saving') : t('common.save')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
